import React, { useEffect, useReducer } from 'react';
import { useParams, NavLink, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { IoIosArrowBack } from "react-icons/io";
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import EmailSettingsForm from "./EmailSettingsForm/EmailSettingsForm";

import api from './../../../api';

import { toastrAdd } from './../../../actions';

const initialState = {
    email: {},
    loading: true,
    initialized: false,
    error: false,
    sending: false,
    isEnabled: false
}

const reducer = (state, action) => {
    switch(action.type) {
        case 'LOADING':
            return {
                ...state,
                loading: action.loading
            }
        case 'FETCH_EMAIL':
            return {
                ...state,
                initialized: false,
                loading: true
            }
        case 'FETCH_EMAIL_SUCCESS':
            return {
                ...state,
                email: action.email,
                loading: false,
                initialized: true,
                error: false
            }
        case 'FETCH_EMAIL_FAIL':
            return {
                ...state,
                email: action.email,
                loading: false,
                initialized: true,
                error: action.error
            }
        case 'SAVE_EMAIL':
            return {
                ...state,
                loading: true,
                error: false
            }
        case 'SAVE_EMAIL_SUCCESS':
            return {
                ...state,
                email: action.email,
                loading: false,
                error: false
            }
        case 'SAVE_EMAIL_FAIL':
            return {
                ...state,
                loading: false,
                error: action.error
            }
        case 'FETCH_TEMPLATE_EMAIL':
            return {
                ...state,
                loading: true,
                initialized: false,
            }
        case 'FETCH_TEMPLATE_EMAIL_SUCCESS':
            return {
                ...state,
                loading: false,
                email: {...action.templateEmail, partnerID: action.partnerID},
                initialized: true,
                error: false
            }
        case 'FETCH_TEMPLATE_EMAIL_FAIL':
            return {
                ...state,
                loading: false,
                email: {},
                initialized: true,
                error: action.error
            }
        case 'SEND_EMAIL':
            return {
                ...state,
                loading: true,
                error: false,
                sending: true
            }
        case 'SEND_EMAIL_SUCCESS':
            return {
                ...state,
                loading: false,
                sending: false
            }
        case 'SEND_EMAIL_FAIL':
            return {
                ...state,
                loading: false,
                sending: false,
                error: action.error
            }
        case 'SEND_EMAIL_TEST':
            return {
                ...state,
                loading: true,
                error: false,
                sending: true
            }
        case 'SEND_EMAIL_TEST_SUCCESS':
            return {
                ...state,
                loading: false,
                sending: false
            }
        case 'SEND_EMAIL_TEST_FAIL':
            return {
                ...state,
                loading: false,
                sending: false,
                error: action.error
            }
        case 'SET_IS_ENABLED':
            return {
                ...state,
                isEnabled: action.isEnabled
            }
        default:
            return state;
    }
}

function Email(props) {

    const { partnerEmailId } = useParams();
    const isNew = partnerEmailId === 'create';
    const partnerID = localStorage.getItem("partnerID");
    const dispatch = useDispatch();
    const history = useHistory();
    const { eventID } = props;
    const [ state, stateDispatch ] = useReducer(reducer, initialState);
    const { initialized, loading, email, error, saving, isEnabled, /* sending */ } = state;
    const { t } = useTranslation();

    /**
     * Fetch email.
     *
     * @param {string} partnerEmailId
     */
    const _fetchEmail = partnerEmailId  => {
        stateDispatch({type: 'FETCH_EMAIL'});
        api.email.getEmail(partnerEmailId).then(response => {
            stateDispatch({type: 'FETCH_EMAIL_SUCCESS', email: response.data.data.partnerEmail });
        }).catch(error => {
            stateDispatch({type: 'FETCH_EMAIL_FAIL', error: error.firstError });
        })
    }

    /**
     * Fetch email.
     *
     */
    const _fetchTemplateEmail = (eventID, partnerID, checkIfEnabled = false) => {
        stateDispatch({type: 'FETCH_TEMPLATE_EMAIL'});
        api.email.getEmailTemplate(eventID, partnerID).then(response => {
            if (!checkIfEnabled) {
                // Delete partnerEmailId so we do not overwrite it.
                delete response.data.data.templateEmail.partnerEmailId;
                stateDispatch({type: 'FETCH_TEMPLATE_EMAIL_SUCCESS', templateEmail: response.data.data.templateEmail, partnerID: partnerID });
            } else {
                stateDispatch({ type: 'SET_IS_ENABLED', isEnabled: response.data.data.templateEmail.isEnabled });
            }
        }).catch(error => {
            stateDispatch({type: 'FETCH_TEMPLATE_EMAIL_FAIL', error: error.errors[0] });
        })
    }

    /**
     * Save email.
     *
     * @param {object} data
     */
    const saveEmail = data => {
        stateDispatch({type: 'SAVE_EMAIL'});
        api.email.saveEmail(data).then(response => {
            stateDispatch({type: 'SAVE_EMAIL_SUCCESS', email: response.data.data.partnerEmail});
            dispatch(toastrAdd({code: 0, details: t('emails.save_success_details'), title: t('emails.save_success_title'), type: 'success'}));
            if(partnerEmailId === 'create') {
                history.push(`/events/${eventID}/emails/${response.data.data.partnerEmail.partnerEmailId}`);
            }
        })
        .catch(error => {
            stateDispatch({type: 'SAVE_EMAIL_FAIL', error: error.errors[0]});
            dispatch(toastrAdd({code: 0, details: `${t('general.details')}: ${error.errors[0].details}`, title: t('errors.general_form_error_title'), type: 'error'}));
        })
    }

    /**
     * Save email.
     *
     * @param {object} data
     */
    const sendEmail = data => {
        stateDispatch({type: 'SEND_EMAIL'});
        api.email.saveEmail(data).then(responseA => {
            stateDispatch({type: 'SAVE_EMAIL_SUCCESS', email: responseA.data.data.partnerEmail});
            api.email.sendEmail(email.partnerEmailId).then(response => {
                stateDispatch({type: 'SEND_EMAIL_SUCCESS'});
                dispatch(toastrAdd({code: 0, details: t('emails.send_success_details'), title: t('emails.send_success_title'), type: 'success'}));
                // Send user back to emails list when send is successful.
                history.push(`/events/${eventID}/emails`);
            })
            .catch(error => {
                stateDispatch({type: 'SEND_EMAIL_FAIL', error: error.errors[0]});
                dispatch(toastrAdd({code: 0, details: `${t('general.details')}: ${error.errors[0].details}`, title: t('emails.send_fail_title'), type: 'error'}));
            })
        })
        .catch(error => {
            stateDispatch({type: 'SEND_EMAIL_TEST_FAIL', error: error.errors[0]});
            dispatch(toastrAdd({code: 0, details: `${t('general.details')}: ${error.errors[0].details}`, title: t('errors.general_form_error_title'), type: 'error'}));
        }) 
    }

    /**
     * Fetch email on component mount.
     */
    useEffect(() => {
        if(partnerEmailId && !isNew && eventID) {
            _fetchEmail(partnerEmailId)
            _fetchTemplateEmail(eventID, partnerID, true);
        } else {
            _fetchTemplateEmail(eventID, partnerID)
        }
    }, [partnerEmailId, eventID, partnerID, isNew])

    return (
        <React.Fragment>

            <div className="row">
                <div className="col-12">
                    <NavLink to={`/events/${eventID}/emails`} style={{position: 'relative', top: '-10px', background: 'white', padding: '6px 15px' }}>
                        <IoIosArrowBack /> {t('emails.back_to_emails')}
                    </NavLink>
                </div>
                <div className="col">
                    {!initialized &&
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="sr-only">{t('general.loading')}</span>
                        </Spinner>
                    }
                </div>
            </div>

            {error &&
                <Alert variant="danger" style={{marginTop: '20px'}}>
                    <strong>{error.title}</strong><br />
                    <span>{error.details}</span>
                </Alert>
            }

            <CSSTransition
                in={(initialized)}
                timeout={800}
                classNames="FadeIn"
                unmountOnExit
            >
                <Card>
                    <Card.Header className="border-bottom">
                        {isNew ? t('general.create_new') : t('general.edit')}
                    </Card.Header>
                    <Card.Body>
                        <EmailSettingsForm
                            loading={loading}
                            email={email}
                            saving={saving}
                            saveEmail={saveEmail}
                            sendEmail={sendEmail}
                            isNew={isNew}
                            isEnabled={isEnabled}
                        />
                    </Card.Body>
                </Card>
            </CSSTransition>

        </React.Fragment>
    )
}

export default Email;